import { Injectable } from '@angular/core';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError, switchMap, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ApiCallService } from '@services/api-call.service';
import { PlatformService } from '@core/services/platform.service';
import * as CacheActions from '@shared/store/cache.actions';
import { selectCachedData } from '@shared/store/cache.selectors';
const CACHE_KEY_PREFIX = 'app_cache_';

interface SimpleCacheOptions {
  onSuccess?: (res: any) => void;
  onError?: (err: any) => void;
  useStore?: boolean;
  expiry?: number;
  refreshOnBrowser?: boolean;
}

interface CachedData {
  data: any;
  timestamp: number;
  expiry?: number;
}

interface CacheState {
  [key: string]: CachedData;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  constructor(
    private transferState: TransferState,
    private apiCallService: ApiCallService,
    private platformService: PlatformService,
    private store: Store<{ cache: CacheState }>
  ) {}

  /** Create consistent prefixed cache key */
  private createCacheKey(key: string): string {
    return `${CACHE_KEY_PREFIX}${key}`;
  }

  /** Validate cached data based on expiry */
  private isDataValid(cachedData: CachedData): boolean {
    if (!cachedData.expiry) return true;
    return Date.now() - cachedData.timestamp < cachedData.expiry;
  }

  /** Get from store */
  private getFromStore(cacheKey: string): Observable<any | null> {
    return this.store.select(selectCachedData(cacheKey)).pipe(
      take(1),
      map(cached => (cached && this.isDataValid(cached) ? cached.data : null))
    );
  }

  /** Set in store */
  private setInStore(cacheKey: string, data: any, expiry?: number): void {
    const cachedData: CachedData = {
      data,
      timestamp: Date.now(),
      expiry
    };
    this.store.dispatch(CacheActions.setData({ key: cacheKey, data: cachedData }));
  }

  /** ---------------------- GET ---------------------- */
  get<T = any>(
    apiUrl: string,
    cacheKey: string,
    options?: SimpleCacheOptions,
    loader = true,
    responseType?: any
  ): Observable<T> {
    const prefixedKey = this.createCacheKey(cacheKey);
    const stateKey = makeStateKey<T>(prefixedKey);

    /** 1️⃣ Check SSR TransferState first */
    if (this.transferState.hasKey(stateKey)) {
      const tsData = this.transferState.get<T>(stateKey, null as any);

      if (tsData !== null) {
        /** FIX: REMOVE TRANSFERSTATE IMMEDIATELY ON BROWSER */
        if (this.platformService.isBrowser && !options?.useStore) {
          this.transferState.remove(stateKey);
        }

        options?.onSuccess?.(tsData);

        /** Browser refresh needed? */
        if (this.platformService.isBrowser && options?.refreshOnBrowser) {
          this.makeApiCall<T>(apiUrl, prefixedKey, options, false, responseType)
            .pipe(take(1))
            .subscribe();
        }

        return of(tsData);
      }
    }

    /** 2️⃣ Check local NgRx store (browser only + useStore enabled) */
    if (options?.useStore && this.platformService.isBrowser) {
      return this.getFromStore(prefixedKey).pipe(
        switchMap(data => {
          if (data) {
            options?.onSuccess?.(data);
            return of(data);
          }
          return this.makeApiCall<T>(apiUrl, prefixedKey, options, loader, responseType);
        }),
        catchError(err => {
          options?.onError?.(err);
          return throwError(() => err);
        })
      );
    }

    /** 3️⃣ Default: Make fresh API call */
    return this.makeApiCall<T>(apiUrl, prefixedKey, options, loader, responseType);
  }

  /** ---------------------- API CALL (GET) ---------------------- */
  private makeApiCall<T = any>(
    apiUrl: string,
    cacheKey: string,
    options?: SimpleCacheOptions,
    loader: boolean = true,
    responseType?: any
  ): Observable<T> {
    const stateKey = makeStateKey<T>(cacheKey);

    return this.apiCallService.get(apiUrl, loader, {}, responseType).pipe(
      tap(res => {
        options?.onSuccess?.(res);

        /** Store in TransferState only on server */
        if (this.platformService.isServer) {
          this.transferState.set(stateKey, res);
        }

        /** Store in NgRx store on browser */
        if (options?.useStore && this.platformService.isBrowser) {
          this.setInStore(cacheKey, res, options.expiry);
        }
      }),
      catchError(err => {
        options?.onError?.(err);
        return throwError(() => err);
      })
    );
  }

  /** ---------------------- POST ---------------------- */
  post<T = any>(apiUrl: string, payload: any, cacheKey: string, options?: SimpleCacheOptions): Observable<T> {
    const prefixedKey = this.createCacheKey(cacheKey);
    const stateKey = makeStateKey<T>(prefixedKey);

    /** SSR TransferState */
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<T>(stateKey, null as any);

      if (data !== null && this.platformService.isBrowser) {
        this.transferState.remove(stateKey);
      }

      options?.onSuccess?.(data);
      return of(data);
    }

    /** NgRx store */
    if (options?.useStore && this.platformService.isBrowser) {
      return this.getFromStore(prefixedKey).pipe(
        switchMap(data => {
          if (data) {
            options?.onSuccess?.(data);
            return of(data);
          }
          return this.makePostApiCall(apiUrl, payload, prefixedKey, options);
        })
      );
    }

    return this.makePostApiCall(apiUrl, payload, prefixedKey, options);
  }

  /** ---------------------- API CALL (POST) ---------------------- */
  private makePostApiCall<T = any>(
    apiUrl: string,
    payload: any,
    cacheKey: string,
    options?: SimpleCacheOptions
  ): Observable<T> {
    const stateKey = makeStateKey<T>(cacheKey);

    return this.apiCallService.post(apiUrl, payload, true).pipe(
      tap(res => {
        options?.onSuccess?.(res);

        if (this.platformService.isServer) {
          this.transferState.set(stateKey, res);
        }

        if (options?.useStore && this.platformService.isBrowser) {
          this.setInStore(cacheKey, res, options.expiry);
        }
      }),
      catchError(err => {
        options?.onError?.(err);
        return throwError(() => err);
      })
    );
  }

  /** ---------------------- PUT ---------------------- */
  put<T = any>(apiUrl: string, payload: any, loader: boolean = true): Observable<T> {
    return this.apiCallService.put(apiUrl, payload, loader);
  }

  /** ---------------------- DELETE ---------------------- */
  delete<T = any>(apiUrl: string, loader: boolean = true): Observable<T> {
    return this.apiCallService.delete(apiUrl, loader);
  }

  /** ---------------------- CLEAR CACHE ---------------------- */
  clearCache(cacheKey: string, clearFromStore = false): void {
    const prefixedKey = this.createCacheKey(cacheKey);
    const stateKey = makeStateKey(prefixedKey);

    this.transferState.remove(stateKey);

    if (clearFromStore && this.platformService.isBrowser) {
      this.store.dispatch(CacheActions.clearData({ key: prefixedKey }));
    }
  }

  /** ---------------------- CHECK CACHE ---------------------- */
  hasCache(cacheKey: string, checkStore = false): Observable<boolean> {
    const prefixedKey = this.createCacheKey(cacheKey);
    const stateKey = makeStateKey(prefixedKey);

    if (this.transferState.hasKey(stateKey)) return of(true);

    if (checkStore && this.platformService.isBrowser) {
      return this.store.select(selectCachedData(prefixedKey)).pipe(
        take(1),
        map(cached => !!cached && this.isDataValid(cached))
      );
    }

    return of(false);
  }

  /** ---------------------- CLEAR ALL CACHE ---------------------- */
  clearAllCache(): void {
    if (this.platformService.isBrowser) {
      this.store.dispatch(CacheActions.clearAll());
    }
  }

  cleanExpiredCache(): void {
    if (this.platformService.isBrowser) {
      this.store.dispatch(CacheActions.cleanExpired());
    }
  }

  getFromCache<T = any>(cacheKey: string): Observable<T | null> {
    const prefixedKey = this.createCacheKey(cacheKey);
    if (this.platformService.isBrowser) {
      return this.getFromStore(prefixedKey);
    }
    return of(null);
  }
}
